import { type ClinicianTypeInput } from '@/lib/types';
import { type ClinicSize } from '@heidi/growth/contracts';
import { type OrganisationRole, type SpellingPreference } from '@heidi/schema/ml-scribe';
import { type CountryType } from '@heidi/utils/countries';
import { getCountry } from '@heidi/utils/get-country';

import type { UseDictateOnboardingAnalyticsReturn } from '@heidi/dictate';
import { type DictateStep } from '@heidi/dictate';

export type { ClinicSize };

export interface DictateFormData {
  firstName: string;
  lastName: string;
  specialty: ClinicianTypeInput | null;
  otherSpecialty: string;
  role: OrganisationRole | null;
  country: CountryType | null;
  jurisdiction: string;
  languages: string[];
  /** English spelling variant; null until the user picks one (defaults to UK). */
  spellingPreference: SpellingPreference | null;
  clinicName: string;
  clinicSize: ClinicSize | null;
  emrEhr: string | null;
}

export const EMPTY_DICTATE_FORM_DATA: DictateFormData = {
  firstName: '',
  lastName: '',
  specialty: null,
  otherSpecialty: '',
  role: null,
  country: null,
  jurisdiction: '',
  // Language step seeds when empty: profile dictation → OS locale → English (UK).
  // Intentionally not ['en'] here so non-English locales (e.g. fr) can preselect.
  languages: [],
  spellingPreference: null,
  clinicName: '',
  clinicSize: null,
  emrEhr: null,
};

/** Initial form state — country from timezone (same as onboarding v2). */
export function getInitialDictateFormData(): DictateFormData {
  return {
    ...EMPTY_DICTATE_FORM_DATA,
    country: getCountry(),
  };
}

export type DictateStepCompletedExtra = Parameters<
  UseDictateOnboardingAnalyticsReturn['trackStepCompleted']
>[2];

export interface GoToNextStepOptions {
  /** Advance without firing `onboarding_step_completed` (e.g. languages "Set up later"). */
  skipStepCompletion?: boolean;
}

/** Full unauth page tour vs authenticated desktop modal lite tour. */
export type DictateOnboardingVariant = 'full' | 'lite';

export interface DictateOnboardingContextValue {
  /** Distinguishes disk/sign-in full flow from authenticated modal lite. */
  variant: DictateOnboardingVariant;
  currentStep: DictateStep;
  formData: DictateFormData;
  progressPercentage: number;
  canGoBack: boolean;
  isCapturingHotkey: boolean;
  /** The chord currently captured in the hotkey-capture form, or null if none. */
  capturedChord: string[] | null;
  /** Hotkey confirmed during onboarding (disk-persisted pre-login). */
  selectedHotkey: string[] | null;
  /** True when key-selection has a valid captured hotkey (`DictationPreview` visible). */
  keySelectionHasValidHotkey: boolean;
  /** False until persisted step/hotkey have been read from disk on cold start. */
  hasRestoredFromDisk: boolean;
  setFormData: (patch: Partial<DictateFormData>) => void;
  setIsCapturingHotkey: (value: boolean) => void;
  setCapturedChord: (chord: string[] | null) => void;
  setSelectedHotkey: (chord: string[] | null) => void;
  setKeySelectionHasValidHotkey: (value: boolean) => void;
  goToNextStep: (
    extra?: DictateStepCompletedExtra,
    options?: GoToNextStepOptions
  ) => void;
  goToPreviousStep: () => void;
  goToStep: (step: DictateStep, extra?: DictateStepCompletedExtra) => void;
  completeOnboarding: () => void;
  /** Fires step completion for the current step without advancing (e.g. sign-in redirect). */
  completeCurrentStep: (extra?: DictateStepCompletedExtra) => void;
  analytics: Pick<
    UseDictateOnboardingAnalyticsReturn,
    'trackStepSkipped' | 'trackOnboardingCompleted'
  >;
}
